import React from 'react';
import { NuqsAdapter } from 'nuqs/adapters/react-router';
import { onError } from '@apollo/client/link/error';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { ApolloProvider } from '@apollo/react-hooks';
import { removeTypenameFromVariables } from '@apollo/client/link/remove-typename';
import { ApolloClient, from, HttpLink, InMemoryCache } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { ErrorBoundary } from 'react-error-boundary';
import { CookiesProvider } from 'react-cookie';
import { AuthContextProvider } from './contexts';
import './App.css';
import { theme } from './Theme';
import {
  Welcome,
  Application,
  ApplicationCardList,
  DRCAssessment,
  DRCConditionCheck,
  ForgotPassword,
  FRCAssessment,
  FRCAssessmentDetail,
  GSAssessment,
  GSAssessmentDetail,
  Monitor,
  Nomination,
  ResetPassword,
  Signup,
  SignupSuccessful,
  SSOHome,
  SupervisorMonitorList,
  UserInformation,
  ARQualificationCheckFormPage,
  PanelHomePage,
  ARProcess,
  GSPanelDetails,
  HoDDoSAssessment,
  HoDDoSAssessmentDetail,
  RefereeForm,
  RefereeFormExpired,
  RefereeFormSuccessful,
  DRCApprovalDetail,
  InterviewDetailPage,
  InterviewPanelHome,
  LoginPage,
  DRCAssessmentDetail,
  PanelDetailReviewVersion,
  ViewSituation,
  DRCApproval,
  PolishEvaluation,
} from './pages';
import {
  AppFooter,
  AppHeader,
  ProtectedRoute,
  ROLES_MODULE,
  SSOProtectedRoute,
  ErrorPage,
  SystemNote,
  SystemNotification,
} from './components';
import ErrorFallback from './components/ErrorFallback/ErrorFallback';
import { MockUser } from './pages/Management/Developer';
import { PICSPage } from './pages/Signup/components';
import { SuperAdminHomePage } from './pages/Management/SuperAdmin/SuperAdminHomePage';
import { SuperAdminMonitor } from './pages/Management/SuperAdmin/SuperAdminMonitor';
import { GPAHelperPage } from './pages/Application/components';
import {
  CoSupervisorNomination,
  TokenProtectLogin,
} from './pages/Nomination/components';
import { OrganizationManagement } from './pages/Management/OrganizationManagement';
import { FacultyForm } from './pages/Management/OrganizationManagement/FacultyForm';
import { DepartmentForm } from './pages/Management/OrganizationManagement/DepartmentForm';

import { MakeUpDeclaration } from './pages/Application/components/ApplicationForm';
import { convertString2Date } from './utils';

const removeTypenameLink = removeTypenameFromVariables();

const enum protectType {
  APPLICANT_PROTECTION = 'APPLICANT_PROTECTION',
  STAFF_PROTECTION = 'STAFF_PROTECTION',
  PUBLIC_OPEN = 'PUBLIC_OPEN',
}
type RoutePackage = {
  path: string;
  roles: ROLES_MODULE[];
  protected: protectType;
  element: React.ReactNode;
};

const routeSwitch = {
  LOGIN_ON: process.env.REACT_APP_LOGIN_ON === 'true',
  SIGN_UP_ON: process.env.REACT_APP_SIGN_UP_ON === 'true',
  APPLICATION_ON: process.env.REACT_APP_APPLICATION_ON === 'true',
  NOMINATION_ON: process.env.REACT_APP_NOMINATION_ON === 'true',
  MONITOR_VIEW_ON: process.env.REACT_APP_MONITOR_VIEW_ON === 'true',
  ASSESSMENT_ON: process.env.REACT_APP_ASSESSMENT_ON === 'true',
  ACADEMIC_REGISTRY_ON: process.env.REACT_APP_ACADEMIC_REGISTRY_ON === 'true',
  PANEL_ON: process.env.REACT_APP_PANEL_ON === 'true',
  DISABLE_CAPTCHA: process.env.REACT_APP_DISABLE_CAPTCHA === 'true',
};

const routesModuleMap: Partial<Record<string, RoutePackage[]>> = {
  LOGIN_ON: [
    {
      path: '/',
      roles: [ROLES_MODULE.NA],
      protected: protectType.PUBLIC_OPEN,
      element: (
        <LoginPage
          staffLogin={false}
          envDisableCaptcha={routeSwitch.DISABLE_CAPTCHA}
        />
      ),
    },
    {
      path: '/notice',
      roles: [ROLES_MODULE.NA],
      protected: protectType.PUBLIC_OPEN,
      element: <Welcome />,
    },
    {
      path: '/login',
      roles: [ROLES_MODULE.NA],
      protected: protectType.PUBLIC_OPEN,
      element: (
        <LoginPage
          staffLogin={false}
          envDisableCaptcha={routeSwitch.DISABLE_CAPTCHA}
        />
      ),
    },
    {
      path: '/internal/login',
      roles: [ROLES_MODULE.NA],
      protected: protectType.PUBLIC_OPEN,
      element: (
        <LoginPage
          staffLogin={true}
          envDisableCaptcha={routeSwitch.DISABLE_CAPTCHA}
        />
      ),
    },
    {
      path: '/forgot-password',
      roles: [ROLES_MODULE.NA],
      protected: protectType.PUBLIC_OPEN,
      element: <ForgotPassword />,
    },
    {
      path: '/reset-password',
      roles: [ROLES_MODULE.NA],
      protected: protectType.PUBLIC_OPEN,
      element: <ResetPassword />,
    },
  ],
  SIGN_UP_ON: [
    {
      path: '/signup',
      roles: [ROLES_MODULE.NA],
      protected: protectType.PUBLIC_OPEN,
      element: <Signup />,
    },
    {
      path: '/signup/pics',
      roles: [ROLES_MODULE.NA],
      protected: protectType.PUBLIC_OPEN,
      element: <PICSPage />,
    },
    {
      path: '/home',
      roles: [ROLES_MODULE.NA],
      protected: protectType.PUBLIC_OPEN,
      element: <SSOHome />,
    },
    {
      path: '/signup/successful',
      roles: [ROLES_MODULE.NA],
      protected: protectType.PUBLIC_OPEN,
      element: <SignupSuccessful />,
    },
  ],
  APPLICATION_ON: [
    {
      path: '/applicant/:applicantId',
      roles: [ROLES_MODULE.NA],
      protected: protectType.APPLICANT_PROTECTION,
      element: <UserInformation />,
    },
    {
      path: '/applications',
      roles: [ROLES_MODULE.NA],
      protected: protectType.APPLICANT_PROTECTION,
      element: <ApplicationCardList />,
    },
    {
      path: '/application/create',
      roles: [ROLES_MODULE.NA],
      protected: protectType.APPLICANT_PROTECTION,
      element: <Application />,
    },
    {
      path: '/application/make-up-declaration',
      roles: [ROLES_MODULE.NA],
      protected: protectType.APPLICANT_PROTECTION,
      element: <ErrorPage errorCode={404} />,
    },
    {
      path: '/application/make-up-declaration/:applicationId',
      roles: [ROLES_MODULE.NA],
      protected: protectType.APPLICANT_PROTECTION,
      element: <MakeUpDeclaration />,
    },
    {
      path: '/application/:applicationId',
      roles: [ROLES_MODULE.NA],
      protected: protectType.APPLICANT_PROTECTION,
      element: <Application />,
    },
    {
      path: '/referee-report',
      roles: [ROLES_MODULE.NA],
      protected: protectType.PUBLIC_OPEN,
      element: <RefereeForm />,
    },
    {
      path: '/referee-report/successful',
      roles: [ROLES_MODULE.NA],
      protected: protectType.PUBLIC_OPEN,
      element: <RefereeFormSuccessful />,
    },
    {
      path: '/referee-report/expired',
      roles: [ROLES_MODULE.NA],
      protected: protectType.PUBLIC_OPEN,
      element: <RefereeFormExpired />,
    },
    {
      path: '/gpa-helperpage',
      roles: [ROLES_MODULE.NA],
      protected: protectType.PUBLIC_OPEN,
      element: <GPAHelperPage />,
    },
  ],
  NOMINATION_ON: [
    {
      path: '/internal/nominations',
      roles: [ROLES_MODULE.SUPERVISOR],
      protected: protectType.STAFF_PROTECTION,
      element: <SupervisorMonitorList />,
    },
    {
      path: '/internal/nominations/application-list',
      roles: [ROLES_MODULE.SUPERVISOR],
      protected: protectType.STAFF_PROTECTION,
      element: <SupervisorMonitorList />,
    },
    {
      path: '/internal/nomination',
      roles: [ROLES_MODULE.SUPERVISOR],
      protected: protectType.STAFF_PROTECTION,
      element: <Nomination />,
    },
    {
      path: '/internal/create-nomination/:applicationId',
      roles: [ROLES_MODULE.SUPERVISOR],
      protected: protectType.STAFF_PROTECTION,
      element: <Nomination />,
    },
    {
      path: '/internal/nomination/:nominationId',
      roles: [ROLES_MODULE.SUPERVISOR],
      protected: protectType.STAFF_PROTECTION,
      element: <Nomination />,
    },
    {
      path: '/nomination-co',
      roles: [ROLES_MODULE.SUPERVISOR],
      protected: protectType.PUBLIC_OPEN,
      element: <TokenProtectLogin />,
    },
    {
      path: '/nomination/co/:applicationId/:nominationId',
      roles: [ROLES_MODULE.SUPERVISOR],
      protected: protectType.STAFF_PROTECTION,
      element: <CoSupervisorNomination />,
    },
    {
      path: '/internal/polish-comments/:applicationId',
      roles: [
        ROLES_MODULE.SUPERVISOR,
        ROLES_MODULE.RIGHT_DO_RPG_S,
        ROLES_MODULE.RIGHT_DO_RPG_S_GO,
        ROLES_MODULE.RIGHT_DO_RPG_S_SEC,
      ],
      protected: protectType.STAFF_PROTECTION,
      element: <PolishEvaluation />,
    },
  ],
  MONITOR_VIEW_ON: [
    {
      path: '/internal/monitor/:applicationId',
      roles: Object.values(ROLES_MODULE).filter(role => {
        return role !== ROLES_MODULE.NA;
      }),
      protected: protectType.STAFF_PROTECTION,
      element: <Monitor />,
    },
  ],
  ASSESSMENT_ON: [
    {
      path: '/internal/interview-homepage',
      roles: [ROLES_MODULE.RIGHT_INTERVIEW_PANEL],
      protected: protectType.STAFF_PROTECTION,
      element: <InterviewPanelHome />,
    },
    {
      path: '/internal/interview-detail/:applicationId',
      roles: [ROLES_MODULE.RIGHT_INTERVIEW_PANEL],
      protected: protectType.STAFF_PROTECTION,
      element: <InterviewDetailPage />,
    },
    {
      path: '/internal/dorpgs-homepage',
      roles: [
        ROLES_MODULE.RIGHT_DO_RPG_S,
        ROLES_MODULE.RIGHT_DO_RPG_S_GO,
        ROLES_MODULE.RIGHT_DO_RPG_S_SEC,
      ],
      protected: protectType.STAFF_PROTECTION,
      element: <DRCAssessment />,
    },
    {
      path: '/internal/dorpgs-homepage/dorpgsassessment/details/:applicationId',
      roles: [
        ROLES_MODULE.RIGHT_DO_RPG_S,
        ROLES_MODULE.RIGHT_DO_RPG_S_GO,
        ROLES_MODULE.RIGHT_DO_RPG_S_SEC,
      ],
      protected: protectType.STAFF_PROTECTION,
      element: <DRCAssessmentDetail />,
    },
    {
      path: '/internal/dorpgs-homepage/dorpgsassessment/condition-check/:applicationId',
      roles: [
        ROLES_MODULE.RIGHT_DO_RPG_S,
        ROLES_MODULE.RIGHT_DO_RPG_S_GO,
        ROLES_MODULE.RIGHT_DO_RPG_S_SEC,
        ROLES_MODULE.RIGHT_GS,
      ],
      protected: protectType.STAFF_PROTECTION,
      element: <DRCConditionCheck />,
    },

    {
      path: '/internal/drc-homepage',
      roles: [
        ROLES_MODULE.RIGHT_DRC,
        ROLES_MODULE.RIGHT_DRC_CHAIR,
        ROLES_MODULE.RIGHT_DRC_GO,
        ROLES_MODULE.RIGHT_DRC_SEC,
      ],
      protected: protectType.STAFF_PROTECTION,
      element: <DRCApproval />,
    },
    {
      path: '/internal/drc-homepage/drcapproval/details/:applicationId',
      roles: [
        ROLES_MODULE.RIGHT_DRC,
        ROLES_MODULE.RIGHT_DRC_CHAIR,
        ROLES_MODULE.RIGHT_DRC_GO,
        ROLES_MODULE.RIGHT_DRC_SEC,
      ],
      protected: protectType.STAFF_PROTECTION,
      element: <DRCApprovalDetail />,
    },
    {
      path: '/internal/frc-homepage',
      protected: protectType.STAFF_PROTECTION,
      roles: [
        ROLES_MODULE.RIGHT_FRC,
        ROLES_MODULE.RIGHT_FRC_CHAIR,
        ROLES_MODULE.RIGHT_FRC_GO,
        ROLES_MODULE.RIGHT_FRC_SEC,
      ],
      element: <FRCAssessment />,
    },
    {
      path: '/internal/frc-homepage/frcassessments',
      roles: [
        ROLES_MODULE.RIGHT_FRC,
        ROLES_MODULE.RIGHT_FRC_CHAIR,
        ROLES_MODULE.RIGHT_FRC_GO,
        ROLES_MODULE.RIGHT_FRC_SEC,
      ],
      protected: protectType.STAFF_PROTECTION,
      element: <FRCAssessment />,
    },
    {
      path: '/internal/frc-homepage/frcassessment/details/:applicationId',
      roles: [
        ROLES_MODULE.RIGHT_FRC,
        ROLES_MODULE.RIGHT_FRC_CHAIR,
        ROLES_MODULE.RIGHT_FRC_GO,
        ROLES_MODULE.RIGHT_FRC_SEC,
      ],
      protected: protectType.STAFF_PROTECTION,
      element: <FRCAssessmentDetail />,
    },
    {
      path: '/internal/gs-homepage',
      roles: [
        ROLES_MODULE.RIGHT_GS,
        ROLES_MODULE.RIGHT_GSB,
        ROLES_MODULE.RIGHT_GSB_CHAIR,
        ROLES_MODULE.RIGHT_GSB_GO,
        ROLES_MODULE.RIGHT_GSB_SEC,
      ],
      protected: protectType.STAFF_PROTECTION,
      element: <GSAssessment />,
    },
    {
      path: '/internal/gs-homepage/gsassessments',
      roles: [
        ROLES_MODULE.RIGHT_GS,
        ROLES_MODULE.RIGHT_GSB,
        ROLES_MODULE.RIGHT_GSB_CHAIR,
        ROLES_MODULE.RIGHT_GSB_GO,
        ROLES_MODULE.RIGHT_GSB_SEC,
      ],
      protected: protectType.STAFF_PROTECTION,
      element: <GSAssessment />,
    },
    {
      path: '/internal/gs-homepage/gsassessment/details/:applicationId',
      roles: [
        ROLES_MODULE.RIGHT_GS,
        ROLES_MODULE.RIGHT_GSB,
        ROLES_MODULE.RIGHT_GSB_CHAIR,
        ROLES_MODULE.RIGHT_GSB_GO,
        ROLES_MODULE.RIGHT_GSB_SEC,
      ],
      protected: protectType.STAFF_PROTECTION,
      element: <GSAssessmentDetail />,
    },
    {
      path: '/internal/gs-homepage/gspanel/details/:panelId',
      roles: [
        ROLES_MODULE.RIGHT_GS,
        ROLES_MODULE.RIGHT_GSB,
        ROLES_MODULE.RIGHT_GSB_CHAIR,
        ROLES_MODULE.RIGHT_GSB_GO,
        ROLES_MODULE.RIGHT_GSB_SEC,
      ],
      protected: protectType.STAFF_PROTECTION,
      element: <GSPanelDetails viewSituation={ViewSituation.GS} />,
    },
    {
      path: '/internal/hod-dos-homepage',
      roles: [ROLES_MODULE.RIGHT_HOD_DOS, ROLES_MODULE.RIGHT_HOD_DOS_SEC],
      protected: protectType.STAFF_PROTECTION,
      element: <HoDDoSAssessment />,
    },
    {
      path: '/internal/hod-dos-homepage/hoddosassessments',
      roles: [ROLES_MODULE.RIGHT_HOD_DOS, ROLES_MODULE.RIGHT_HOD_DOS_SEC],
      protected: protectType.STAFF_PROTECTION,
      element: <HoDDoSAssessment />,
    },
    {
      path: '/internal/hod-dos-homepage/hoddosassessment/:applicationId',
      roles: [ROLES_MODULE.RIGHT_HOD_DOS, ROLES_MODULE.RIGHT_HOD_DOS_SEC],
      protected: protectType.STAFF_PROTECTION,
      element: <HoDDoSAssessmentDetail />,
    },
    {
      path: '/internal/mock-user',
      roles: [
        ROLES_MODULE.RIGHT_GS,
        ROLES_MODULE.RIGHT_GSB,
        ROLES_MODULE.RIGHT_GSB_CHAIR,
        ROLES_MODULE.RIGHT_GSB_GO,
        ROLES_MODULE.RIGHT_GSB_SEC,
      ],
      protected: protectType.STAFF_PROTECTION,
      element: <MockUser />,
    },

    {
      path: '/internal/super-admin',
      roles: [
        ROLES_MODULE.RIGHT_GS,
        ROLES_MODULE.RIGHT_GSB,
        ROLES_MODULE.RIGHT_GSB_CHAIR,
        ROLES_MODULE.RIGHT_GSB_GO,
        ROLES_MODULE.RIGHT_GSB_SEC,
        ROLES_MODULE.RIGHT_AR,
      ],
      protected: protectType.STAFF_PROTECTION,
      element: <SuperAdminHomePage />,
    },
    {
      path: '/internal/super-admin/user-management',
      roles: [
        ROLES_MODULE.RIGHT_GS,
        ROLES_MODULE.RIGHT_GSB,
        ROLES_MODULE.RIGHT_GSB_CHAIR,
        ROLES_MODULE.RIGHT_GSB_GO,
        ROLES_MODULE.RIGHT_GSB_SEC,
        ROLES_MODULE.RIGHT_AR,
      ],
      protected: protectType.STAFF_PROTECTION,
      element: <SuperAdminMonitor />,
    },
    {
      path: '/internal/super-admin/organization-management',
      roles: [
        ROLES_MODULE.RIGHT_GS,
        ROLES_MODULE.RIGHT_GSB,
        ROLES_MODULE.RIGHT_GSB_CHAIR,
        ROLES_MODULE.RIGHT_GSB_GO,
        ROLES_MODULE.RIGHT_GSB_SEC,
        ROLES_MODULE.RIGHT_AR,
      ],
      protected: protectType.STAFF_PROTECTION,
      element: <OrganizationManagement />,
    },
    {
      path: '/internal/super-admin/organization-management/faculty/add',
      roles: [
        ROLES_MODULE.RIGHT_GS,
        ROLES_MODULE.RIGHT_GSB,
        ROLES_MODULE.RIGHT_GSB_CHAIR,
        ROLES_MODULE.RIGHT_GSB_GO,
        ROLES_MODULE.RIGHT_GSB_SEC,
        ROLES_MODULE.RIGHT_AR,
      ],
      protected: protectType.STAFF_PROTECTION,
      element: <FacultyForm />,
    },
    {
      path: '/internal/super-admin/organization-management/faculty/edit/:id',
      roles: [
        ROLES_MODULE.RIGHT_GS,
        ROLES_MODULE.RIGHT_GSB,
        ROLES_MODULE.RIGHT_GSB_CHAIR,
        ROLES_MODULE.RIGHT_GSB_GO,
        ROLES_MODULE.RIGHT_GSB_SEC,
        ROLES_MODULE.RIGHT_AR,
      ],
      protected: protectType.STAFF_PROTECTION,
      element: <FacultyForm />,
    },
    {
      path: '/internal/super-admin/organization-management/department/add',
      roles: [
        ROLES_MODULE.RIGHT_GS,
        ROLES_MODULE.RIGHT_GSB,
        ROLES_MODULE.RIGHT_GSB_CHAIR,
        ROLES_MODULE.RIGHT_GSB_GO,
        ROLES_MODULE.RIGHT_GSB_SEC,
        ROLES_MODULE.RIGHT_AR,
      ],
      protected: protectType.STAFF_PROTECTION,
      element: <DepartmentForm />,
    },
    {
      path: '/internal/super-admin/organization-management/department/edit/:id',
      roles: [
        ROLES_MODULE.RIGHT_GS,
        ROLES_MODULE.RIGHT_GSB,
        ROLES_MODULE.RIGHT_GSB_CHAIR,
        ROLES_MODULE.RIGHT_GSB_GO,
        ROLES_MODULE.RIGHT_GSB_SEC,
        ROLES_MODULE.RIGHT_AR,
      ],
      protected: protectType.STAFF_PROTECTION,
      element: <DepartmentForm />,
    },
    {
      path: '/internal/super-admin/configuration-management',
      roles: [
        ROLES_MODULE.RIGHT_GS,
        ROLES_MODULE.RIGHT_GSB,
        ROLES_MODULE.RIGHT_GSB_CHAIR,
        ROLES_MODULE.RIGHT_GSB_GO,
        ROLES_MODULE.RIGHT_GSB_SEC,
        ROLES_MODULE.RIGHT_AR,
      ],
      protected: protectType.STAFF_PROTECTION,
      element: <SuperAdminMonitor />,
    },
    {
      path: '/internal/super-admin/adhoc-tools',
      roles: [
        ROLES_MODULE.RIGHT_GS,
        ROLES_MODULE.RIGHT_GSB,
        ROLES_MODULE.RIGHT_GSB_CHAIR,
        ROLES_MODULE.RIGHT_GSB_GO,
        ROLES_MODULE.RIGHT_GSB_SEC,
        ROLES_MODULE.RIGHT_AR,
      ],
      protected: protectType.STAFF_PROTECTION,
      element: <SuperAdminMonitor />,
    },
    {
      path: '/internal/super-admin/applicant-information-update',
      roles: [
        ROLES_MODULE.RIGHT_GS,
        ROLES_MODULE.RIGHT_GSB,
        ROLES_MODULE.RIGHT_GSB_CHAIR,
        ROLES_MODULE.RIGHT_GSB_GO,
        ROLES_MODULE.RIGHT_GSB_SEC,
        ROLES_MODULE.RIGHT_AR,
      ],
      protected: protectType.STAFF_PROTECTION,
      element: <SuperAdminMonitor />,
    },
  ],
  ACADEMIC_REGISTRY_ON: [
    {
      path: '/internal/ar-homepage',
      roles: [ROLES_MODULE.RIGHT_AR],
      protected: protectType.STAFF_PROTECTION,
      element: <ARProcess />,
    },
    {
      path: '/internal/ar-homepage/ar-process',
      roles: [ROLES_MODULE.RIGHT_AR],
      protected: protectType.STAFF_PROTECTION,
      element: <ARProcess />,
    },
    {
      path: '/internal/ar-homepage/ar-process/:applicationId',
      roles: [ROLES_MODULE.RIGHT_AR],
      protected: protectType.STAFF_PROTECTION,
      element: <ARQualificationCheckFormPage />,
    },
  ],
  PANEL_ON: [
    {
      path: '/internal/panel-homepage',
      roles: [ROLES_MODULE.RIGHT_PANEL],
      protected: protectType.STAFF_PROTECTION,
      element: <PanelHomePage />,
    },
    {
      path: '/internal/panel-homepage/panel-details/:panelId',
      roles: [ROLES_MODULE.RIGHT_PANEL],
      protected: protectType.STAFF_PROTECTION,
      element: <GSPanelDetails viewSituation={ViewSituation.MEMBER_NORMAL} />,
    },
    {
      path: '/internal/panel-homepage/panel-review-list/:panelId',
      roles: [ROLES_MODULE.RIGHT_PANEL],
      protected: protectType.STAFF_PROTECTION,
      element: <PanelDetailReviewVersion />,
    },
  ],
};

const authLink = setContext((_, { headers }) => {
  const tokens = localStorage.getItem('tokens');
  const currentRole = localStorage.getItem('currentRole');

  return {
    headers: {
      ...headers,
      'current-role': currentRole ?? '',
      authorization: tokens ?? '',
    },
  };
});

const errorLink = onError(({ graphQLErrors }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ extensions }) => {
      if (extensions?.originalError?.statusCode === 401) {
        const currentPath = window.location.pathname;

        let redirectPath = '/login';
        for (const moduleRoutes of Object.values(routesModuleMap)) {
          for (const route of moduleRoutes ?? []) {
            if (route.path === currentPath) {
              if (route.protected === protectType.STAFF_PROTECTION) {
                redirectPath = '/internal/login';
              }
              break;
            }
          }
        }

        localStorage.removeItem('tokens');
        window.location.href = redirectPath;
      }
    });
  }
});

const httpLink = new HttpLink({
  uri: '/graphql',
});

const client = new ApolloClient({
  cache: new InMemoryCache({
    addTypename: false,
  }),

  link: from([errorLink, removeTypenameLink, authLink.concat(httpLink)]),
});

const routeFilter = (
  routesModuleMap: Partial<Record<string, RoutePackage[]>>,
  RouteSwitch: Record<string, boolean>,
): RoutePackage[] => {
  const filteredRoutes: RoutePackage[] = [];
  for (const key in routesModuleMap) {
    if (RouteSwitch[key]) {
      const routes = routesModuleMap[key];
      if (routes) {
        filteredRoutes.push(...routes);
      }
    }
  }

  return filteredRoutes;
};

function App(): React.ReactElement {
  const filteredRoutes = routeFilter(routesModuleMap, routeSwitch);

  const protectTypeToComponent: Record<
    protectType,
    (element: React.ReactNode, roles?: ROLES_MODULE[]) => React.ReactNode
  > = {
    [protectType.APPLICANT_PROTECTION]: element => (
      <ProtectedRoute redirectPath="/login">{element}</ProtectedRoute>
    ),
    [protectType.STAFF_PROTECTION]: (element, roles) => (
      <SSOProtectedRoute role={roles ?? []} redirectPath="/internal/login">
        {element}
      </SSOProtectedRoute>
    ),
    [protectType.PUBLIC_OPEN]: element => <>{element}</>,
  };

  const systemUpgradeStartDate = process.env
    .REACT_APP_UPGRADE_START_DATE as string;
  const systemUpgradeEndDate = process.env.REACT_APP_UPGRADE_END_DATE as string;

  const presentTime = new Date().getTime();
  const systemUpgradeOn =
    presentTime >
      new Date(
        systemUpgradeStartDate ?? '2024-11-08T16:00:00.000Z',
      ).getTime() &&
    presentTime <
      new Date(systemUpgradeEndDate ?? '2024-11-12T16:00:00.000Z').getTime();

  const systemNotificationContent =
    process.env.REACT_APP_SYSTEM_NOTIFICATION_CONTENT;
  const systemNotificationStartTime = convertString2Date(
    process.env.REACT_APP_SYSTEM_NOTIFICATION_START_TIME,
  );
  const systemNotificationEndTime = convertString2Date(
    process.env.REACT_APP_SYSTEM_NOTIFICATION_END_TIME,
  );

  return (
    <ThemeProvider theme={theme}>
      <ApolloProvider client={client}>
        <NuqsAdapter>
          <Router>
            <AuthContextProvider>
              <CookiesProvider defaultSetOptions={{ path: '/' }}>
                <ErrorBoundary FallbackComponent={ErrorFallback}>
                  <div
                    style={{
                      position: 'relative',
                      maxWidth: '100%',
                      width: '100vw',
                      display: 'flex',
                      flexDirection: 'column',
                      minHeight: '100vh',
                    }}
                  >
                    <AppHeader navigationItems={[]} settingsItems={[]} />
                    <SystemNotification
                      content={systemNotificationContent}
                      period={{
                        start: systemNotificationStartTime,
                        end: systemNotificationEndTime,
                      }}
                    />
                    <SystemNote />
                    <div
                      style={{
                        flex: 1,
                      }}
                    >
                      <Routes>
                        {/* TODO replace main page */}
                        {systemUpgradeOn ? (
                          <Route path="/*" element={<Welcome />} />
                        ) : (
                          <>
                            {filteredRoutes.map((route, index) => {
                              const Component =
                                protectTypeToComponent[route.protected];
                              return (
                                <Route
                                  key={index}
                                  path={route.path}
                                  element={Component(
                                    route.element,
                                    route.roles,
                                  )}
                                />
                              );
                            })}
                            <Route
                              path="/*"
                              element={<ErrorPage errorCode={0} />}
                            />
                            <Route
                              path="/outroles"
                              element={<ErrorPage errorCode={403} />}
                            />
                          </>
                        )}
                      </Routes>
                    </div>
                    <div
                      style={{
                        position: 'relative',
                        bottom: 0,
                      }}
                    >
                      <AppFooter />
                    </div>
                  </div>
                </ErrorBoundary>
              </CookiesProvider>
            </AuthContextProvider>
          </Router>
        </NuqsAdapter>
      </ApolloProvider>
    </ThemeProvider>
  );
}

export default App;
